import { KeyboardEvent, useContext, useEffect, useRef, useState } from "react";
import ApiErrorMessages from "../components/UI/ApiErrorMessages";
import Loader from "../components/UI/Loader";
import { formatJSONDateWithoutTime } from "../utils/dateHelpers";
import './ProcessPublicSubmissions.css';
import { format, startOfMonth, startOfYear } from "date-fns";
import PublicIntakeSubmission from "../models/serviceModels/PublicIntakeSubmission";
import useElementVisible from "../hooks/useElementVisible";
import { getForm, getFormInstance, getPublicSubmissionsResults, getPublicSubmissionsSummary, markPublicSubmissionProcessed, markPublicSubmissionUnprocessed } from "../services/formsService";
import FormInstance from "../models/serviceModels/FormInstance";
import Button from "../components/UI/Button";
import FormEditor from "../components/Form/Editor/FormEditor";
import EditableFormField from "../models/pageModels/EditableFormField";
import { mapFormFieldToEditableFormField } from "../mappers/formFieldMapper";
import getclassNames from "../utils/getClassNames";
import Form from "../models/serviceModels/Form";
import Modal from "../components/UI/Modal";
import CreateOrEditClientModel from "../models/pageModels/CreateOrEditClientModel";
import { CHECKBOX_GROUP_ID, RADIO_BUTTON_ID, REPEATING_STRUCTURED_ID, SELECT_LIST_ID } from "../constants/fieldTypes";
import StructuredFieldCollection from "../models/pageModels/StructuredFieldCollection";
import CreateOrEditHouseholdMemberModel from "../models/pageModels/CreateOrEditHouseholdMemberModel";
import ParticipantForm from "../components/AddParticipant/ParticpantForm";
import IconButton from "../components/UI/IconButton";
import RadioButton from "../components/UI/RadioButton";
import { OrganizationContext } from "../App";
import { getDemographicsRaceMode } from "../utils/demographicsSchema";
import PublicSubmissionDetailTabs, { PublicSubmissionDetailTabsHandle } from "../components/PublicSubmissions/PublicSubmissionDetailTabs";
import { PublicSubmissionFollowUpsResponse } from "../models/serviceModels/PublicSubmissionFollowUp";
import { publicSubmissionFollowUpsEnabled } from "../utils/features";
import AttachPublicSubmissionModal from "../components/PublicSubmissions/AttachPublicSubmissionModal";
import { getVisibleSubmissionFormFields } from "../utils/publicSubmissionFields";
import {
    filterPublicSubmissionStatusItems,
    getPublicSubmissionFilteredOutCount,
    getPublicSubmissionRequestFilters,
    isFollowUpPublicSubmissionStatus
} from "../utils/publicSubmissionFeature";
import Dropdown from "../components/UI/Dropdown";

const TAKE = 20;
const MAX_CACHE_LENGTH = 10;

const normalizeSubmittedRaceSelections = (
    ethnicityValue: { label: string; value: string } | undefined,
    raceValues: { label: string; value: string }[],
    organization: any
) => {
    const races = Array.isArray(raceValues) ? raceValues : [];
    const ethnicityId = ethnicityValue?.value || '';

    if (getDemographicsRaceMode(organization) === 'combined') {
        if (races.some(race => race.value?.startsWith('e:') || race.value?.startsWith('r:') || race.value?.startsWith('omb:'))) {
            return {
                ethnicityId: '',
                raceIds: races
            };
        }

        if (ethnicityValue?.value) {
            return {
                ethnicityId: '',
                raceIds: [{
                    ...ethnicityValue,
                    value: `e:${ethnicityValue.value}`
                }]
            };
        }

        return {
            ethnicityId: '',
            raceIds: races.map(race => ({
                ...race,
                value: `r:${race.value}`
            }))
        };
    }

    const submittedEthnicity = races.find(race => race.value?.startsWith('e:'));
    return {
        ethnicityId: ethnicityId || submittedEthnicity?.value?.substring(2) || '',
        raceIds: races
            .filter(race => !race.value?.startsWith('e:'))
            .map(race => race.value?.startsWith('r:')
                ? { ...race, value: race.value.substring(2) }
                : race)
    };
};

const ProcessPublicSubmissions = () => {
    const organization = useContext(OrganizationContext);
    const [loading, setLoading] = useState(false);
    const [results, setResults] = useState<PublicIntakeSubmission[]>([]);
    const [searching, setSearching] = useState(false);
    const [skip, setSkip] = useState(0);
    const [totalCount, setTotalCount] = useState(0);
    const [paging, setPaging] = useState(false);
    const [reference, visible] = useElementVisible<HTMLDivElement>();
    const [apiError, setApiError] = useState<any>();
    const [filtering, setFiltering] = useState(false);
    const [selectedSubmission, setSelectedSubmission] = useState<PublicIntakeSubmission>();
    const [selectedSubmissionInstance, setSelectedSubmissionInstance] = useState<FormInstance>();
    const [submissionCache, setSubmissionCache] = useState<{ id: string, instance: FormInstance, form: Form }[]>([]);
    const [instanceFields, setInstanceFields] = useState<EditableFormField[]>([]);
    const [loadingSubmission, setLoadingSubmission] = useState(false);
    const [showCreateParticipant, setShowCreateParticipant] = useState(false);
    const [createParticipantModel, setCreateParticipantModel] = useState<CreateOrEditClientModel>();
    const [createParticipantForm, setCreateParticipantForm] = useState<Form>();
    const [createParticipantFormFields, setCreateParticipantFormFields] = useState<EditableFormField[]>();
    const [participantFormErrors, setParticipantFormErrors] = useState<any>();
    const [markingProcessed, setMarkingProcessed] = useState<PublicIntakeSubmission>();
    const [markingUnprocessed, setMarkingUnprocessed] = useState(false);
    const [confirmMarkUnprocessed, setConfirmMarkUnprocessed] = useState<PublicIntakeSubmission>();
    const [modalErrors, setModalErrors] = useState<any>();
    const [summary, setSummary] = useState<{ status: string; allTimeCount: number; filteredCount: number; }[]>();
    const [filters, setFilters] = useState<string[]>(['new']);
    const loaded = useRef(false);
    const [submissionFilter, setSubmissionFilter] = useState('month_to_date');
    const [selectedSubmissionCanResolve, setSelectedSubmissionCanResolve] = useState(!publicSubmissionFollowUpsEnabled);
    const [showAttachParticipant, setShowAttachParticipant] = useState(false);
    const submissionDetailTabsRef = useRef<PublicSubmissionDetailTabsHandle>(null);

    useEffect(() => {
        document.title = "Public Submissions"
    }, []);

    const handleOnProcess = (submission: PublicIntakeSubmission) => {
        if (publicSubmissionFollowUpsEnabled && !selectedSubmissionCanResolve) {
            return;
        }
        setMarkingProcessed(submission);
        markPublicSubmissionProcessed({
            formInstanceId: submission.formInstanceId
        }).then(() => {
            setSkip(0);
            handleOnSearch();
            getPublicSubmissionsSummary(convertDateSubmissionFilter(submissionFilter)).then(r => {
                setSummary(filterPublicSubmissionStatusItems(r.results, publicSubmissionFollowUpsEnabled));
            }).catch(e => {
                console.error(e);
                setApiError(e);
            });
        }).catch(e => {
            setApiError(e);
            console.error(e);
        }).finally(() => {
            setMarkingProcessed(undefined);
        });
    };

    const markUnprocessed = (formInstanceId: string, intendToDelete: boolean) => {
        return markPublicSubmissionUnprocessed({
            formInstanceId,
            intendToDelete
        }).then(() => {
            setSkip(0);
            handleOnSearch();
            getPublicSubmissionsSummary(convertDateSubmissionFilter(submissionFilter)).then(r => {
                setSummary(filterPublicSubmissionStatusItems(r.results, publicSubmissionFollowUpsEnabled));
            }).catch(e => {
                console.error(e);
                setApiError(e);
            });
            return;
        });
    }

    const handleOnUnprocess = () => {
        if (!confirmMarkUnprocessed) {
            return;
        }

        if (confirmMarkUnprocessed.revertRestricted) {
            console.warn('Revert is restricted from the modal.');
            return;
        }

        setMarkingUnprocessed(true);
        markUnprocessed(confirmMarkUnprocessed.formInstanceId, true).then(() => {
            handleCancellingConfirmMarkUnprocessed();
        }).catch(e => {
            setModalErrors(e);
            console.error(e);
        }).finally(() => {
            setMarkingUnprocessed(false);
        });
    }

    const handleOnRevertProcessed = (submission: PublicIntakeSubmission) => {
        if (submission.revertRestricted) {
            console.warn('Revert is restricted from result.');
            return;
        }

        setMarkingUnprocessed(false);
        setModalErrors(undefined);

        if (submission.clientId) {
            setConfirmMarkUnprocessed(submission);
        } else {
            setApiError(undefined);
            setMarkingUnprocessed(true);
            markUnprocessed(submission.formInstanceId, false).catch(e => {
                setApiError(e);
                console.error(e);
            }).finally(() => {
                setMarkingUnprocessed(false);
            });
        }
    };

    const handleCancellingConfirmMarkUnprocessed = () => {
        setConfirmMarkUnprocessed(undefined);
        setMarkingUnprocessed(false);
        setModalErrors(undefined);
    }

    const handleOnCreateParticipantSave = () => {
        setSkip(0);
        handleOnSearch();
        getPublicSubmissionsSummary(convertDateSubmissionFilter(submissionFilter)).then(r => {
            setSummary(filterPublicSubmissionStatusItems(r.results, publicSubmissionFollowUpsEnabled));
        }).catch(e => {
            console.error(e);
            setApiError(e);
        });
    };

    const handleOnCancelCreatingParticipant = () => {
        setShowCreateParticipant(false);
        setParticipantFormErrors(undefined);
        setCreateParticipantFormFields(undefined);
        setCreateParticipantForm(undefined);
        setCreateParticipantModel(undefined);
    };

    const convertDateSubmissionFilter = (dateFilter: string) => {
        if (!dateFilter) {
            return undefined;
        }

        if (dateFilter === 'all_time') {
            return '';
        }

        if (dateFilter === 'month_to_date') {
            return startOfMonth(new Date()).toISOString();
        }

        if (dateFilter === 'year_to_date') {
            return startOfYear(new Date()).toISOString();
        }

        console.warn(`${dateFilter} is unknown. Defaulting to all_time`);
        return '';
    }

    const handleOnSearch = (determinedFilters?: string[], determinedSubmissionFilter?: string) => {
        const requestFilters = getPublicSubmissionRequestFilters(
            determinedFilters || filters,
            publicSubmissionFollowUpsEnabled
        );
        setSearching(true);
        setSkip(0);
        setApiError(undefined);
        handleOnCanceledSubmissionSelection();
        getPublicSubmissionsResults({
            filters: requestFilters,
            skip: 0,
            sortBy: 'submitted',
            sortDirection: 'desc',
            take: TAKE,
            fromDate: determinedSubmissionFilter ? convertDateSubmissionFilter(determinedSubmissionFilter) : convertDateSubmissionFilter(submissionFilter)
        }).then(r => {
            setResults(filterPublicSubmissionStatusItems(r.data, publicSubmissionFollowUpsEnabled));
            setTotalCount(r.totalCount);
        }).catch(e => {
            console.error(e);
            setApiError(e);
        }).finally(() => {
            setSearching(false);
            setFiltering(false);
        });
    };

    const handleStartingCreateParticipant = () => {
        if (!selectedSubmission || (publicSubmissionFollowUpsEnabled && !selectedSubmissionCanResolve)) {
            return;
        }

        const matchingCachedSubmission = submissionCache.find(sc => sc.id === selectedSubmission.id);
        if (!matchingCachedSubmission) {
            return;
        }

        setShowCreateParticipant(true);
        setParticipantFormErrors(undefined);

        setCreateParticipantForm(matchingCachedSubmission.form);
        const additionalInfoFields: EditableFormField[] = matchingCachedSubmission.form.fields.filter(f => f.name && f.name.startsWith("additional_info_")).map(ff => {
            const instanceField = matchingCachedSubmission.instance.instanceFields.find(iff => iff.name === ff.name);
            const fieldCopy = {
                ...ff,
                name: ff.name.replace("additional_info_", "")
            }
            return mapFormFieldToEditableFormField(fieldCopy, instanceField?.value || '', instanceField?.id || '', instanceField?.files || []);
        });
        const getFieldValue = (fieldName: string, defaultValue: any, fieldsSource: { name: string, value: string, fieldTypeId: number }[]): any => {
            const matchingInstanceField = fieldsSource.find(f => f.name === fieldName);
            if (!matchingInstanceField || !matchingInstanceField.value) {
                return defaultValue;
            }

            if (matchingInstanceField.fieldTypeId === RADIO_BUTTON_ID ||
                matchingInstanceField.fieldTypeId === CHECKBOX_GROUP_ID ||
                matchingInstanceField.fieldTypeId === SELECT_LIST_ID ||
                matchingInstanceField.fieldTypeId === REPEATING_STRUCTURED_ID) {
                const parsedValue = JSON.parse(matchingInstanceField.value);
                return parsedValue;
            }

            return matchingInstanceField.value;
        };
        const getHouseholdMembersFieldValue = () => {
            const fieldValue = getFieldValue('main_profile_family_members', [], matchingCachedSubmission.instance.instanceFields);
            if (!fieldValue) {
                return [];
            }

            const parsedValueCollections: StructuredFieldCollection[] = fieldValue;
            const housememberModels: CreateOrEditHouseholdMemberModel[] = [];
            for (const fieldsCollection of parsedValueCollections) {
                const demographics = normalizeSubmittedRaceSelections(
                    getFieldValue("ethnicity", {}, fieldsCollection.fields),
                    getFieldValue("race", [], fieldsCollection.fields),
                    organization.value
                );

                housememberModels.push({
                    firstName: getFieldValue("first_name", '', fieldsCollection.fields),
                    lastName: getFieldValue("last_name", '', fieldsCollection.fields),
                    middleName: getFieldValue("middle_name", '', fieldsCollection.fields),
                    dateOfBirth: getFieldValue("date_of_birth", '', fieldsCollection.fields),
                    ethnicityId: demographics.ethnicityId,
                    raceIds: demographics.raceIds,
                    genderId: getFieldValue("gender", {}, fieldsCollection.fields)?.value || '',
                    relationshipId: getFieldValue("relationship", {}, fieldsCollection.fields)?.value || '',
                    phoneNumber: getFieldValue("phone_number", "", fieldsCollection.fields),
                    email: getFieldValue("email", '', fieldsCollection.fields)
                });
            }

            return housememberModels;
        };
        const demographics = normalizeSubmittedRaceSelections(
            getFieldValue("main_profile_ethnicity", {}, matchingCachedSubmission.instance.instanceFields),
            getFieldValue("main_profile_race", [], matchingCachedSubmission.instance.instanceFields),
            organization.value
        );
        
        setCreateParticipantFormFields(additionalInfoFields);
        setCreateParticipantModel({
            id: '',
            firstName: getFieldValue("main_profile_first_name", '', matchingCachedSubmission.instance.instanceFields),
            lastName: getFieldValue("main_profile_last_name", '', matchingCachedSubmission.instance.instanceFields),
            middleName: getFieldValue("main_profile_middle_name", '', matchingCachedSubmission.instance.instanceFields),
            dateOfBirth: getFieldValue("main_profile_date_of_birth", "", matchingCachedSubmission.instance.instanceFields),
            genderId: getFieldValue("main_profile_gender", {}, matchingCachedSubmission.instance.instanceFields)?.value || '',
            ethnicityId: demographics.ethnicityId,
            raceIds: demographics.raceIds,
            pronouns: getFieldValue("main_profile_preferred_pronouns", "", matchingCachedSubmission.instance.instanceFields),
            addressLine1: getFieldValue("main_profile_address_line_1", "", matchingCachedSubmission.instance.instanceFields),
            addressLine2: getFieldValue("main_profile_address_line_2", "", matchingCachedSubmission.instance.instanceFields),
            addressCity: getFieldValue("main_profile_city", "", matchingCachedSubmission.instance.instanceFields),
            addressState: getFieldValue("main_profile_state", "", matchingCachedSubmission.instance.instanceFields)?.value || '',
            addressZipCode: getFieldValue("main_profile_zip_code", "", matchingCachedSubmission.instance.instanceFields),
            phoneNumber: getFieldValue("main_profile_phone_number", "", matchingCachedSubmission.instance.instanceFields),
            email: getFieldValue("main_profile_email", "", matchingCachedSubmission.instance.instanceFields),
            householdMembers: getHouseholdMembersFieldValue(),
            deletedHouseholdMembers: [],
            activated: format(new Date(), 'yyyy-MM-dd'),
            enrolled: format(new Date(), 'yyyy-MM-dd'),
            selectedPrograms: []
        });
    };

    const handleOnKeyUpOnFilter = (e: KeyboardEvent<HTMLDivElement>, currentFilter: string) => {
        if (e.code === 'Space' || e.code === 'Enter') {
            handleOnFilterChange(currentFilter);
        }
    }

    const handleOnFilterChange = (selectedFilter: string) => {
        if (!publicSubmissionFollowUpsEnabled && isFollowUpPublicSubmissionStatus(selectedFilter)) {
            return;
        }

        let newFilters = null;
        if (filters.includes(selectedFilter)) {
            newFilters = filters.filter(f => f !== selectedFilter);
        } else {
            newFilters = [
                ...filters,
                selectedFilter
            ];
        }

        setFiltering(true);
        setFilters(newFilters);
        handleOnSearch(newFilters);
    };

    const handleOnCanceledSubmissionSelection = () => {
        setLoadingSubmission(false);
        setSelectedSubmission(undefined);
        setSelectedSubmissionInstance(undefined);
        setInstanceFields([]);
        setApiError(undefined);
        setSelectedSubmissionCanResolve(!publicSubmissionFollowUpsEnabled);
        setShowAttachParticipant(false);
        handleOnCancelCreatingParticipant();
    };

    const handleOnSubmissionSelected = (submission: PublicIntakeSubmission) => {
        setLoadingSubmission(true);
        setSelectedSubmission(submission);
        setSelectedSubmissionCanResolve(!publicSubmissionFollowUpsEnabled);

        const matchingCachedSubmission = submissionCache.find(sc => sc.id === submission.id);
        if (matchingCachedSubmission) {
            const visibleFormFields = getVisibleSubmissionFormFields(matchingCachedSubmission.form.fields, organization.value);
            setSelectedSubmissionInstance({
                ...matchingCachedSubmission.instance
            });

            setInstanceFields([
                ...visibleFormFields.map(ff => {
                    const instanceField = matchingCachedSubmission.instance.instanceFields.find(iff => iff.name === ff.name);
                    return mapFormFieldToEditableFormField(ff, instanceField?.value || '', instanceField?.id || '', instanceField?.files || []);
                })
            ]);

            setLoadingSubmission(false);
        } else {
            Promise.all([getForm(submission.formId, submission.formVersionId), getFormInstance(submission.formId, submission.formInstanceId)]).then(([f, fi]) => {
                const visibleFormFields = getVisibleSubmissionFormFields(f.fields, organization.value);
                setSelectedSubmissionInstance(fi);

                setInstanceFields([
                    ...visibleFormFields.map(ff => {
                        const instanceField = fi.instanceFields.find(iff => iff.name === ff.name);
                        return mapFormFieldToEditableFormField(ff, instanceField?.value || '', instanceField?.id || '', instanceField?.files || []);
                    })
                ]);

                // put new submission at the front so it's faster to find later
                submissionCache.unshift({
                    id: submission.id,
                    instance: fi,
                    form: f
                });

                // shrink cache if we are beyond the limit
                while (submissionCache.length > MAX_CACHE_LENGTH) {
                    submissionCache.pop();
                }

                setSubmissionCache([
                    ...submissionCache
                ]);
            }).catch(e => {
                setApiError(e);
                console.error(e);
            }).finally(() => {
                setLoadingSubmission(false);
            });
        }
    };

    const handleFollowUpsChanged = (followUps: PublicSubmissionFollowUpsResponse) => {
        setSelectedSubmissionCanResolve(followUps.canResolve);
        if (!selectedSubmission) {
            return;
        }

        const openFollowUpCount = followUps.followUps.filter(followUp => followUp.status === 'OPEN').length;
        const submittedFollowUpCount = followUps.followUps.filter(followUp => followUp.status === 'SUBMITTED').length;
        const updatedSubmission = {
            ...selectedSubmission,
            followUpStatus: followUps.followUpStatus,
            followUpCount: followUps.followUps.length,
            openFollowUpCount,
            submittedFollowUpCount
        };
        setSelectedSubmission(updatedSubmission);
        setResults(current => current.map(result => result.id === updatedSubmission.id ? updatedSubmission : result));
    };

    const handleOnDateFilterChanged = (selectedSubmissionFilter: string) => {
        setSubmissionFilter(selectedSubmissionFilter);
        setFiltering(true);

        getPublicSubmissionsSummary(convertDateSubmissionFilter(selectedSubmissionFilter)).then(r => {
            setSummary(filterPublicSubmissionStatusItems(r.results, publicSubmissionFollowUpsEnabled));
        });
        handleOnSearch(filters, selectedSubmissionFilter);
    };

    useEffect(() => {
        if (!loaded.current || skip === 0) {
            return;
        }

        setApiError(undefined);

        setPaging(true);

        getPublicSubmissionsResults({
            filters: getPublicSubmissionRequestFilters(filters, publicSubmissionFollowUpsEnabled),
            skip,
            take: TAKE,
            sortBy: 'submitted',
            sortDirection: 'desc'
        }).then(r => {
            const enabledResults = filterPublicSubmissionStatusItems(r.data, publicSubmissionFollowUpsEnabled);
            setResults(current => {
                if (!current) {
                    return [
                        ...enabledResults
                    ];
                }

                return [
                    ...current,
                    ...enabledResults
                ];
            });
            setTotalCount(r.totalCount)
        }).catch(e => {
            console.error(e);
            setApiError(e);
        }).finally(() => {
            setPaging(false);
        });

    }, [skip]);

    useEffect(() => {
        if (visible && totalCount > 0) {
            setSkip(current => {
                return current + TAKE;
            });
        }
    }, [visible, totalCount]);

    useEffect(() => {
        loaded.current = true;
    }, []);

    useEffect(() => {
        setLoading(true);
        const staringFromDate = convertDateSubmissionFilter('month_to_date')

        Promise.all([getPublicSubmissionsSummary(staringFromDate), getPublicSubmissionsResults({
            filters: ['new'],
            skip: 0,
            take: TAKE,
            sortBy: 'submitted',
            sortDirection: 'desc',
            fromDate: staringFromDate
        })]).then(([rs, rr]) => {
            setSummary(filterPublicSubmissionStatusItems(rs.results, publicSubmissionFollowUpsEnabled));
            setResults(filterPublicSubmissionStatusItems(rr.data, publicSubmissionFollowUpsEnabled));
            setTotalCount(rr.totalCount);
        }).catch(e => {
            console.error(e);
            setApiError(e);
        }).finally(() => {
            setLoading(false);
        });
    }, []);

    const getStatusText = (statusValue: string) => {
        if (!publicSubmissionFollowUpsEnabled && isFollowUpPublicSubmissionStatus(statusValue)) {
            return '';
        }

        let status = '';
        switch (statusValue) {
            case 'reviewed':
                status = 'Reviewed Submissions';
                break;
            case 'new':
                status = 'New Submissions';
                break;
            case 'awaiting_documentation':
                status = 'Awaiting Documentation';
                break;
            case 'documentation_submitted':
                status = 'Documentation Submitted';
                break;
            case 'created_participant':
                status = 'Participants Created';
                break;
            case 'attached_participant':
                status = 'Attached to Participant';
                break;
            case 'hidden':
                status = 'Hidden Submissions';
                break;
        }

        return status;
    };

    const getStatusBadgeText = (statusValue: string) => {
        switch (statusValue) {
            case 'new': return 'New';
            case 'awaiting_documentation': return 'Awaiting Documentation';
            case 'documentation_submitted': return 'Documentation Submitted';
            case 'reviewed': return 'Reviewed';
            case 'created_participant': return 'Participant Created';
            case 'attached_participant': return 'Attached to Participant';
            case 'hidden': return 'Hidden';
            default: return 'Other';
        }
    };

    const statusOrder = [
        'new',
        'awaiting_documentation',
        'documentation_submitted',
        'reviewed',
        'created_participant',
        'attached_participant',
        'hidden'
    ];
    const groupedResults = statusOrder
        .map(status => ({
            status,
            label: getStatusText(status),
            submissions: results.filter(result => result.status === status)
        }))
        .filter(group => group.label && group.submissions.length > 0);
    const knownStatuses = new Set(statusOrder);
    const uncategorizedResults = results.filter(result => !knownStatuses.has(result.status));
    if (uncategorizedResults.length > 0) {
        groupedResults.push({
            status: 'other',
            label: 'Other Submissions',
            submissions: uncategorizedResults
        });
    }

    const selectedSubmissionOptions: { label: string; href?: string; onClick?: () => void; newWindow?: boolean; disabled?: boolean; className?: string; }[] = [];
    if (selectedSubmission) {
        if (publicSubmissionFollowUpsEnabled && !selectedSubmission.processed) {
            selectedSubmissionOptions.push({
                label: 'Request Documentation',
                onClick: () => submissionDetailTabsRef.current?.openRequestDocumentation(),
                disabled: loadingSubmission || !selectedSubmissionInstance
            });
        }
        if (!selectedSubmission.clientId && !selectedSubmission.processed) {
            selectedSubmissionOptions.push({
                label: 'Create Participant',
                onClick: handleStartingCreateParticipant,
                disabled: showCreateParticipant || (publicSubmissionFollowUpsEnabled && !selectedSubmissionCanResolve)
            });
        }
        if (publicSubmissionFollowUpsEnabled && !selectedSubmission.clientId && !selectedSubmission.processed) {
            selectedSubmissionOptions.push({
                label: 'Attach to Participant',
                onClick: () => setShowAttachParticipant(true),
                disabled: !selectedSubmissionCanResolve
            });
        }
        if (selectedSubmission.clientId) {
            selectedSubmissionOptions.push({
                label: 'View on Profile',
                href: `/participants/${selectedSubmission.clientId}/profile-demographics`,
                newWindow: true
            });
        }
        if (!selectedSubmission.processed) {
            selectedSubmissionOptions.push({
                label: 'Mark as Reviewed',
                onClick: () => handleOnProcess(selectedSubmission),
                disabled: markingProcessed !== undefined || (publicSubmissionFollowUpsEnabled && !selectedSubmissionCanResolve)
            });
        }
        if (selectedSubmission.processed && !selectedSubmission.revertRestricted) {
            selectedSubmissionOptions.push({
                label: 'Move to New',
                onClick: () => handleOnRevertProcessed(selectedSubmission),
                disabled: markingUnprocessed,
                className: 'text-danger'
            });
        }
    }

    const newStatusSummary = summary && summary.find(s => s.status === 'new');
    const reviewedStatusSummary = summary && summary.find(s => s.status === 'reviewed');
    const createdParticipantStatusSummary = summary && summary.find(s => s.status === 'created_participant');
    const attachedParticipantStatusSummary = publicSubmissionFollowUpsEnabled
        ? summary?.find(s => s.status === 'attached_participant')
        : undefined;
    const awaitingDocumentationStatusSummary = publicSubmissionFollowUpsEnabled
        ? summary?.find(s => s.status === 'awaiting_documentation')
        : undefined;
    const documentationSubmittedStatusSummary = publicSubmissionFollowUpsEnabled
        ? summary?.find(s => s.status === 'documentation_submitted')
        : undefined;

    const totalFilteredOut = getPublicSubmissionFilteredOutCount(
        summary || [],
        publicSubmissionFollowUpsEnabled
    );

    return (
        <div className="section">
            <section id="main-content" tabIndex={-1}>
                <div className="row justify-content-center">
                    <div className="column-medium-10">
                        <div className="row justify-content-center">
                            <div className="column-auto">
                                <h1>Public Submissions</h1>
                            </div>
                        </div>
                        <ApiErrorMessages error={apiError} />
                        {loading && <Loader />}

                        <div className="row margin-top-5">
                            <div className="column padding-3">
                                <h2>Filters</h2>
                                {summary && <>                                
                                    <div className="row margin-bottom-5 align-items-center">
                                        <div className="column-auto">
                                            <RadioButton id="submission-filters" 
                                                options={[
                                                    { value: 'all_time', label: 'All Time'},
                                                    { value: 'year_to_date', label: 'Year to Date'},
                                                    { value: 'month_to_date', label: 'Month to Date'}
                                                ]}
                                                label="Submissions"
                                                value={submissionFilter}
                                                direction="horizontal"
                                                onChange={handleOnDateFilterChanged}
                                            />
                                        </div>
                                        <div className="column-auto">
                                            {totalFilteredOut} submission(s) filtered out
                                        </div>
                                    </div>
                                    <div className="row">
                                        <div className={publicSubmissionFollowUpsEnabled ? 'column-large-4' : 'column-large-3'}>
                                            <div onClick={() => handleOnFilterChange('new')}
                                                role="checkbox"
                                                tabIndex={0}
                                                aria-label="Filter by New Submissions"
                                                onKeyUp={(e) => handleOnKeyUpOnFilter(e, 'new')}
                                                aria-checked={filters.includes('new') ? "true" : "false"}
                                                className={getclassNames('submission-summary', filters.includes('new') ? 'highlight' : '')}>
                                                <h4>{getStatusText("new")}</h4>
                                                <div className="submission-summary-overall-value">
                                                    {newStatusSummary?.filteredCount || 0}
                                                </div>
                                            </div>
                                        </div>
                                        {publicSubmissionFollowUpsEnabled && <div className="column-large-4">
                                            <div onClick={() => handleOnFilterChange('awaiting_documentation')}
                                                role="checkbox"
                                                tabIndex={0}
                                                aria-label="Filter by Awaiting Documentation"
                                                onKeyUp={(e) => handleOnKeyUpOnFilter(e, 'awaiting_documentation')}
                                                aria-checked={filters.includes('awaiting_documentation') ? "true" : "false"}
                                                className={getclassNames('submission-summary', filters.includes('awaiting_documentation') ? 'highlight' : '')}>
                                                <h4>{getStatusText("awaiting_documentation")}</h4>
                                                <div className="submission-summary-overall-value">
                                                    {awaitingDocumentationStatusSummary?.filteredCount || 0}
                                                </div>
                                            </div>
                                        </div>}
                                        {publicSubmissionFollowUpsEnabled && <div className="column-large-4">
                                            <div onClick={() => handleOnFilterChange('documentation_submitted')}
                                                role="checkbox"
                                                tabIndex={0}
                                                aria-label="Filter by Documentation Submitted"
                                                onKeyUp={(e) => handleOnKeyUpOnFilter(e, 'documentation_submitted')}
                                                aria-checked={filters.includes('documentation_submitted') ? "true" : "false"}
                                                className={getclassNames('submission-summary', filters.includes('documentation_submitted') ? 'highlight' : '')}>
                                                <h4>{getStatusText("documentation_submitted")}</h4>
                                                <div className="submission-summary-overall-value">
                                                    {documentationSubmittedStatusSummary?.filteredCount || 0}
                                                </div>
                                            </div>
                                        </div>}
                                        <div className={publicSubmissionFollowUpsEnabled ? 'column-large-4' : 'column-large-3'}>
                                            <div onClick={() => handleOnFilterChange('reviewed')}
                                                role="checkbox"
                                                tabIndex={0}
                                                aria-label="Filter by Reviewed Submissions"
                                                onKeyUp={(e) => handleOnKeyUpOnFilter(e, 'reviewed')}
                                                aria-checked={filters.includes('reviewed') ? "true" : "false"}
                                                className={getclassNames('submission-summary', filters.includes('reviewed') ? 'highlight' : '')}>
                                                <h4>{getStatusText("reviewed")}</h4>
                                                <div className="submission-summary-overall-value">
                                                    {reviewedStatusSummary?.filteredCount || 0}
                                                </div>
                                            </div>
                                        </div>
                                        <div className={publicSubmissionFollowUpsEnabled ? 'column-large-4' : 'column-large-3'}>
                                            <div onClick={() => handleOnFilterChange('created_participant')}
                                                role="checkbox"
                                                tabIndex={0}
                                                aria-label="Filter by Participants Created"
                                                onKeyUp={(e) => handleOnKeyUpOnFilter(e, 'created_participant')}
                                                aria-checked={filters.includes('created_participant') ? "true" : "false"}
                                                className={getclassNames('submission-summary', filters.includes('created_participant') ? 'highlight' : '')}>
                                                <h4>{getStatusText("created_participant")}</h4>
                                                <div className="submission-summary-overall-value">
                                                    {createdParticipantStatusSummary?.filteredCount || 0}
                                                </div>
                                            </div>
                                        </div>
                                        {publicSubmissionFollowUpsEnabled && <div className="column-large-4">
                                            <div onClick={() => handleOnFilterChange('attached_participant')}
                                                role="checkbox"
                                                tabIndex={0}
                                                aria-label="Filter by Submissions Attached to Participants"
                                                onKeyUp={(e) => handleOnKeyUpOnFilter(e, 'attached_participant')}
                                                aria-checked={filters.includes('attached_participant') ? "true" : "false"}
                                                className={getclassNames('submission-summary', filters.includes('attached_participant') ? 'highlight' : '')}>
                                                <h4>{getStatusText("attached_participant")}</h4>
                                                <div className="submission-summary-overall-value">
                                                    {attachedParticipantStatusSummary?.filteredCount || 0}
                                                </div>
                                            </div>
                                        </div>}
                                    </div>
                                </>}
                            </div>
                        </div>
                        <div className="row margin-top-5">
                            <div className="column padding-3">
                                <h2>Submissions</h2>
                                {filtering && <Loader />}
                                {results && !loading && !searching && !filtering && <>
                                    {results.length === 0 &&
                                        <div className="row justify-content-center">
                                            <div className="column-auto">
                                                <div>No submissions found</div>
                                                <div>{filters.length} filters selected</div>
                                            </div>
                                        </div>}
                                    {results.length !== 0 && <div className="row">
                                        {!showCreateParticipant && <div className="column">
                                            <div className="public-submission-groups">
                                                {groupedResults.map(group => <section className="public-submission-group" key={group.status} aria-labelledby={`submission-group-${group.status}`}>
                                                    <div className="public-submission-group-heading">
                                                        <h3 id={`submission-group-${group.status}`}>{group.label}</h3>
                                                        <span>{group.submissions.length}</span>
                                                    </div>
                                                    <div className="list-item-container">
                                                    {group.submissions.map(d => {
                                                    const statusText = getStatusBadgeText(d.status);
                                                    return <div key={d.id}>
                                                        <div
                                                            className={getclassNames('list-item margin-top-5 public-submission-card', d === selectedSubmission ? 'highlight' : '')}
                                                            data-testid={`public-submission-row-${d.id}`}>
                                                            <div className="public-submission-card-layout">
                                                                <div className="public-submission-card-content">
                                                                    <div className="public-submission-card-title-row">
                                                                        <div className="public-submission-card-title">
                                                                            <h4>{d.formName}</h4>
                                                                            <span className="public-submission-type">Public intake form</span>
                                                                        </div>
                                                                        <span className={`public-submission-status status-${d.status}`}>{statusText}</span>
                                                                    </div>
                                                                    <dl className="public-submission-card-details">
                                                                        <div className="public-submission-participant-name">
                                                                            <dt>Participant</dt>
                                                                            <dd data-testid={`public-submission-name-${d.id}`}>
                                                                                {d.clientId
                                                                                    ? <a href={`/participants/${d.clientId}/profile-demographics`} target="_blank" rel="noopener noreferrer">{d.lastName}, {d.firstName}</a>
                                                                                    : <>{d.lastName}, {d.firstName}</>}
                                                                            </dd>
                                                                        </div>
                                                                        <div>
                                                                            <dt>Submitted</dt>
                                                                            <dd>{format(new Date(d.submissionDate), 'MM/dd/yyyy')}</dd>
                                                                        </div>
                                                                        {d.processed && <div>
                                                                            <dt>Completed</dt>
                                                                            <dd>{d.processedDate && format(new Date(d.processedDate), 'MM/dd/yyyy')}</dd>
                                                                        </div>}
                                                                        {d.dateOfBirth && <div>
                                                                            <dt>Birth Date</dt>
                                                                            <dd>{formatJSONDateWithoutTime(d.dateOfBirth, 'MM/dd/yyyy')}</dd>
                                                                        </div>}
                                                                    </dl>
                                                                </div>
                                                                <div className="public-submission-card-action">
                                                                    <Button id={`open-submission-${d.id}`}
                                                                        onClick={() => handleOnSubmissionSelected(d)}
                                                                        text="View Submission"
                                                                        className="action small"
                                                                        disabled={loadingSubmission}
                                                                        loading={loadingSubmission && selectedSubmission === d} />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                })}
                                                    </div>
                                                </section>)}
                                                {paging && <Loader />}
                                                {results && results.length < totalCount && <div ref={reference} className="padding-5 margin-bottom-5"></div>}
                                            </div>
                                        </div>}
                                        {showCreateParticipant && <div className="column-auto">
                                            <Button id="close-create-participant"
                                                onClick={handleOnCancelCreatingParticipant}
                                                className="small secondary"
                                                text="Cancel Create Participant" />
                                        </div>}
                                        {(selectedSubmissionInstance || loadingSubmission) && selectedSubmission && <div className="column padding-5 border-start" data-testid="selected-public-submission-panel">

                                            <div className="row justify-content-end">
                                                <div className="column-auto">
                                                    <IconButton id="close-submission"
                                                        icon={<i className="ri-close-line"></i>}
                                                        className="flat small"
                                                        onClick={handleOnCanceledSubmissionSelection} />
                                                </div>
                                            </div>
                                            <div className="row justify-content-between align-items-center">
                                                <div className="column-auto" style={{ minWidth: 0, flex: '1 1 auto' }}>
                                                    <h2 style={{ wordWrap: 'break-word', overflowWrap: 'break-word', wordBreak: 'break-word' }}>{selectedSubmission.formName}</h2>
                                                </div>
                                                <div className="column-auto">
                                                    {selectedSubmissionOptions.length > 0 && <Dropdown
                                                        id="public-submission-options"
                                                        optionSize="large"
                                                        alignRight={true}
                                                        options={selectedSubmissionOptions} />}
                                                 </div>
                                             </div>
                                             {publicSubmissionFollowUpsEnabled && !selectedSubmission.processed && !selectedSubmissionCanResolve && <div className="public-submission-follow-up-blocked" role="status">
                                                 Complete or cancel every open documentation request before resolving this submission.
                                             </div>}
                                             {loadingSubmission && <Loader />}
                                             {selectedSubmissionInstance && !loadingSubmission && (publicSubmissionFollowUpsEnabled ? <PublicSubmissionDetailTabs
                                                 ref={submissionDetailTabsRef}
                                                 formInstanceId={selectedSubmission.formInstanceId}
                                                 formName={selectedSubmission.formName}
                                                 instance={selectedSubmissionInstance}
                                                 processed={selectedSubmission.processed}
                                                 onFollowUpsChanged={handleFollowUpsChanged}
                                                 originalContent={<FormEditor
                                                     fields={instanceFields}
                                                     columnNumber={2}
                                                     disabled={true} />} /> : <FormEditor
                                                         fields={instanceFields}
                                                         columnNumber={2}
                                                         disabled={true} />)}
                                        </div>}
                                        {showCreateParticipant && selectedSubmission && !selectedSubmission.clientId && <div className="column padding-5 border-start" data-testid="create-participant-panel">
                                            <div className="row justify-content-end">
                                                <div className="column-auto">
                                                    <IconButton id="close-create-participant"
                                                        icon={<i className="ri-close-line"></i>}
                                                        className="flat small"
                                                        onClick={handleOnCancelCreatingParticipant} />
                                                </div>
                                            </div>
                                            {participantFormErrors && <ApiErrorMessages error={participantFormErrors} />}
                                            <ParticipantForm
                                                existingEditableFormFields={createParticipantFormFields}
                                                existingModel={createParticipantModel}
                                                isEmbedded={true}
                                                onSave={handleOnCreateParticipantSave}
                                                columnNumber={2}
                                                publicFormInstanceId={selectedSubmission.formInstanceId} />
                                        </div>}
                                    </div>}
                                </>}
                            </div>
                        </div>
                    </div>
                </div>
            </section >
            <Modal id="confirm-mark-as-unprocessed" title="Confirm Move to New" visible={confirmMarkUnprocessed !== undefined} className="small" onClose={handleCancellingConfirmMarkUnprocessed}>
                {confirmMarkUnprocessed && <>
                    {modalErrors && <ApiErrorMessages error={modalErrors} />}
                    <div className="row">
                        <div className="column">
                             <div>Submission Name: {confirmMarkUnprocessed.formName}</div>
                             <div>Participant Name: {confirmMarkUnprocessed.lastName}, {confirmMarkUnprocessed.firstName}</div>
                             {confirmMarkUnprocessed.resolutionType === 'ATTACHED_PARTICIPANT' ? <p>Moving this submission to <strong>New</strong> removes its association with the existing participant. The participant and their data will not be changed or deleted.</p> : <>
                                 <p>Moving this submission to <strong>New</strong> will <strong>delete</strong> the created participant and their associated data.</p>
                                 <p className="text-bold">Deleted participants can NOT be recovered.</p>
                             </>}
                             <p>Are you sure you want to move this submission to <strong>New</strong>?</p>
                        </div>
                    </div>
                    <div className="row margin-top-5">
                        <div className="column">
                            <div className="row justify-content-between">
                                <div className="column-medium-auto">
                                    <Button id="cancel-deactivate"
                                        text="No" className="secondary"
                                        disabled={markingUnprocessed}
                                        onClick={handleCancellingConfirmMarkUnprocessed} />
                                </div>
                                <div className="column-medium-auto">
                                    <Button id="confirm-deactivate"
                                        text="Yes"
                                        className="warning"
                                        disabled={markingUnprocessed}
                                        loading={markingUnprocessed}
                                        onClick={handleOnUnprocess} />
                                </div>
                            </div>
                        </div>
                    </div>
                </>}
            </Modal>
            {publicSubmissionFollowUpsEnabled && selectedSubmission && <AttachPublicSubmissionModal
                visible={showAttachParticipant}
                formInstanceId={selectedSubmission.formInstanceId}
                onClose={() => setShowAttachParticipant(false)}
                onAttached={() => {
                    setShowAttachParticipant(false);
                    handleOnCreateParticipantSave();
                }} />}
        </div >
    );
};

export default ProcessPublicSubmissions;
